// ================================================================
// 大師模式 API 模組
// ================================================================

import { apiClient } from './client';

// ================================================================
// 類型定義
// ================================================================

export interface TeacherLoginRequest {
  username: string;
  password: string;
}

export interface Teacher {
  id: number;
  username: string;
  display_name: string | null;
}

export interface TeacherLoginResponse {
  success: boolean;
  message: string;
  token?: string;
  teacher?: Teacher;
}

export interface Consultee {
  id: number;
  teacher_id: number;
  name: string;
  gender: number;
  birth_datetime: string;
  notes: string | null;
  created_at: string;
  consultation_count: number;
}

export interface ConsulteesResponse {
  success: boolean;
  consultees?: Consultee[];
  message?: string;
}

export interface CreateConsulteeRequest {
  name: string;
  gender: number;
  birth_datetime: string;
  notes?: string;
}

export interface CreateConsulteeResponse {
  success: boolean;
  consultee_id?: number;
  message: string;
}

export interface Consultation {
  id: number;
  consultee_id: number;
  question_id: string;
  question_text: string | null;
  answer: string | null;
  consulted_at: string;
}

export interface ConsultationHistoryResponse {
  success: boolean;
  history?: Consultation[];
  message?: string;
}

export interface ConsulteeDetailResponse {
  success: boolean;
  consultee?: Consultee;
  message?: string;
}

export interface AskQuestionRequest {
  question_id: string;
}

export interface AskQuestionResponse {
  success: boolean;
  question_id?: string;
  question_text?: string;
  answer?: string;
  consultation_id?: number;
  message?: string;
}

// ── 配對分析 ──────────────────────────────────────────────────────

export interface PersonInfo {
  birth_time: string;  // YYYYMMDDhh
  name?: string;
  gender: number;      // 1=男 0=女
}

/** PAIR001-025 雙人配對請求 */
export interface PairDualRequest {
  question_id: string;
  person_b: PersonInfo;
}

/** PAIR026-034 多人選一請求 */
export interface PairMultiRequest {
  question_id: string;
  candidates: PersonInfo[];
}

export type PairRequest = PairDualRequest | PairMultiRequest;

export interface PersonInfoResult {
  name: string;
  birth_time: string;
  gender: string;
}

export interface CandidateResult {
  candidate_name: string;
  output: string;
  result: Record<string, unknown> | null;
}

/** 雙人配對回應 */
export interface PairDualResponse {
  success: boolean;
  is_multi_pair: false;
  question_id: string;
  question_text: string;
  output: string;
  person_a_info: PersonInfoResult;
  person_b_info: PersonInfoResult;
  message?: string;
}

/** 多人選一回應 */
export interface PairMultiResponse {
  success: boolean;
  is_multi_pair: true;
  question_id: string;
  question_text: string;
  results: CandidateResult[];
  person_a_info: PersonInfoResult;
  candidates_count: number;
  candidates_names: string[];
  message?: string;
}

export type PairResponse = PairDualResponse | PairMultiResponse;

// ================================================================
// API 函數
// ================================================================

/**
 * 大師登入
 */
export const teacherLogin = async (
  data: TeacherLoginRequest
): Promise<TeacherLoginResponse> => {
  return apiClient.post('/api/teacher/login', data);
};

/**
 * 取得個案列表
 */
export const getConsultees = async (
  search?: string
): Promise<ConsulteesResponse> => {
  const params = search ? { search } : {};
  return apiClient.get('/api/teacher/consultees', { params });
};

/**
 * 新增個案
 */
export const createConsultee = async (
  data: CreateConsulteeRequest
): Promise<CreateConsulteeResponse> => {
  return apiClient.post('/api/teacher/consultees', data);
};

/**
 * 取得單一個案詳細資料
 */
export const getConsultee = async (
  consulteeId: number
): Promise<ConsulteeDetailResponse> => {
  return apiClient.get(`/api/teacher/consultees/${consulteeId}`);
};

/**
 * 為個案執行 Q&A 問答
 */
export const askQuestion = async (
  consulteeId: number,
  data: AskQuestionRequest
): Promise<AskQuestionResponse> => {
  return apiClient.post(`/api/teacher/consultees/${consulteeId}/qa`, data);
};

/**
 * 取得個案諮詢歷史記錄
 */
export const getConsultationHistory = async (
  consulteeId: number
): Promise<ConsultationHistoryResponse> => {
  return apiClient.get(`/api/teacher/consultees/${consulteeId}/history`);
};

/**
 * 大師修改密碼
 */
export const teacherChangePassword = async (
  currentPassword: string,
  newPassword: string
): Promise<{ success: boolean; message: string }> => {
  return apiClient.post('/api/teacher/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
  });
};

/**
 * 為個案執行配對分析（PAIR001-034，不扣款）
 */
export const teacherPairAnalysis = async (
  consulteeId: number,
  data: PairRequest
): Promise<PairResponse> => {
  return apiClient.post(`/api/teacher/consultees/${consulteeId}/pair`, data);
};
